/**
 * subscriptions.js — kilden til sandhed for alle abonnementstyper.
 *
 * Design (per user 01-Jul-2026):
 *   role                 → permissions (user | editor | admin)
 *   subscription_type    → abonnement (free | light | pro)
 *
 * Alle reklame-, popup-, onboarding- og abonnements-visninger læser
 * abonnements-metadata HERFRA. Skal en fordel ændres, en pris justeres
 * eller Light aktiveres kommercielt — ret feltet i denne fil, INTET andet.
 *
 * Light er endnu ikke aktivt kommercielt. `available=false` styrer at
 * kort vises som "Kommer snart" og CTA er deaktiveret. Sæt `available=true`
 * når det er klar til Stripe.
 */

// ---------------------------------------------------------------------------
// Enum
// ---------------------------------------------------------------------------
export const SUBSCRIPTION_TYPES = Object.freeze({
  FREE: 'free',
  LIGHT: 'light',
  PRO: 'pro',
});

// Permissions-roller (adskilt fra abonnement).
export const USER_ROLES = Object.freeze({
  USER: 'user',      // Alm. bruger (dækker også legacy 'guest')
  EDITOR: 'editor',
  ADMIN: 'admin',
});

// Employees (editor + admin) ser ALDRIG reklamer / popups uanset
// abonnementstype. De håndteres separat i visibility-helpers.
const EMPLOYEE_ROLES = new Set(['editor', 'admin']);

// ---------------------------------------------------------------------------
// Definition af hvert abonnement
// ---------------------------------------------------------------------------
export const SUBSCRIPTIONS = Object.freeze({
  free: {
    key: SUBSCRIPTION_TYPES.FREE,
    name: 'Gratis',
    tagline: 'Gratis konto med mere adgang end en gæst',
    price: '0 kr.',
    priceHint: 'Altid gratis',
    available: true,
    comingSoon: false,
    highlight: false,
    features: [
      'Flere funktioner end en anonym gæst',
      'Adgang til udvalgte opskrifter',
      'Op til 2 private opskrifter',
      'Match Finder på egne ingredienser',
      'Diskrete bundreklamer',
    ],
    ctaLabel: 'Fortsæt gratis',
    ctaAction: 'dismiss',
  },
  light: {
    key: SUBSCRIPTION_TYPES.LIGHT,
    name: 'Light',
    tagline: 'Adgang til alle funktioner — uden bundreklamer',
    price: '25 kr.',
    priceHint: '/md',
    available: true,
    comingSoon: false,
    highlight: false,
    // Backend plan-key sendt til /api/billing/checkout — matches PLANS[…]
    // i stripe_routes.py. Light findes kun som månedlig indtil videre.
    planKey: 'light_monthly',
    features: [
      'Adgang til ALLE funktioner',
      'Ingen bundreklamer',
      'Sponsor-rækker mellem opskrifter (sirup, maskiner, bægre, sugerør osv.)',
      'Ubegrænset antal private opskrifter',
      'Sponsorindhold målrettet slushice-udstyr',
    ],
    ctaLabel: 'Vælg Light',
    ctaAction: 'checkout',
  },
  pro: {
    key: SUBSCRIPTION_TYPES.PRO,
    name: 'Pro',
    tagline: 'Alt fra Light — helt uden reklamer',
    price: '49 kr.',
    priceHint: '/md',
    yearlyPrice: '399 kr.',
    yearlyHint: '/år (spar ca. 32 %)',
    available: true,
    comingSoon: false,
    highlight: true,
    highlightLabel: 'Mest populær',
    // planKey er default (monthly); UpgradeModal bytter til 'yearly' hvis
    // brugeren har valgt årlig-toggle. Light har kun én key.
    planKey: 'monthly',
    features: [
      'Alt fra Light',
      'Ingen reklamer overhovedet — hverken bunder eller sponsor-rækker',
      'Fremtidige premium-funktioner kommer primært hertil',
      'Prioriteret support',
      'Opsig når som helst',
    ],
    ctaLabel: 'Opgrader til Pro',
    ctaAction: 'checkout',
  },
});

// Rækkefølge i UI (venstre → højre)
export const SUBSCRIPTION_ORDER = [
  SUBSCRIPTION_TYPES.FREE,
  SUBSCRIPTION_TYPES.LIGHT,
  SUBSCRIPTION_TYPES.PRO,
];

// ---------------------------------------------------------------------------
// User → subscription type helpers
// ---------------------------------------------------------------------------

/**
 * Hent brugerens abonnementstype. Kilde-til-sandhed er backendens
 * `user.subscription_type` (sat af `derive_subscription_type` i auth.py).
 * Fallback for legacy-data / anonymous brugere: udled fra role/pro_status.
 */
export function getSubscriptionType(user) {
  if (!user) return SUBSCRIPTION_TYPES.FREE; // anonym gæst = free-tier
  const explicit = (user.subscription_type || '').toLowerCase();
  if (explicit === SUBSCRIPTION_TYPES.PRO) return SUBSCRIPTION_TYPES.PRO;
  if (explicit === SUBSCRIPTION_TYPES.LIGHT) return SUBSCRIPTION_TYPES.LIGHT;
  if (explicit === SUBSCRIPTION_TYPES.FREE) return SUBSCRIPTION_TYPES.FREE;

  // Legacy fallback via role/pro_status.
  const role = (user.role || '').toLowerCase();
  if (role === 'pro') return SUBSCRIPTION_TYPES.PRO;
  if (role === 'light') return SUBSCRIPTION_TYPES.LIGHT;
  if (user.pro_status === 'active') return SUBSCRIPTION_TYPES.PRO;
  return SUBSCRIPTION_TYPES.FREE;
}

/**
 * True hvis brugeren er ansat (editor eller admin). Employees ser aldrig
 * reklamer / opgraderings-popups uanset deres subscription_type.
 */
export function isEmployee(user) {
  if (!user) return false;
  return EMPLOYEE_ROLES.has((user.role || '').toLowerCase());
}

/**
 * True hvis brugeren har premium-adgang — dvs. Light- eller Pro-abonnement
 * ELLER ansat (editor/admin). Bruges til at gate ALT nuværende betalt indhold
 * (opskrifter, Match, Tips, Favoritter, kommentarer, private opskrifter osv.).
 *
 * Beslutning (per user 08-Jul-2026): Light og Pro har SAMME adgang til alle
 * nuværende funktioner. Forskellen mellem Light og Pro er kun:
 *   • Reklamer (Light ser sponsor-rækker, Pro ser ingenting) — håndteres i
 *     `utils/adVisibility.js`, IKKE her.
 *   • Fremtidige premium-features vil eksklusivt tilhøre Pro; dedikeret
 *     tjek kan bruges via `getSubscriptionType(user) === PRO`.
 *
 * Anonym gæst → false. Registreret free-bruger → false.
 */
export function hasPremiumAccess(user) {
  if (!user) return false;
  if (isEmployee(user)) return true;
  const tier = getSubscriptionType(user);
  return tier === SUBSCRIPTION_TYPES.PRO || tier === SUBSCRIPTION_TYPES.LIGHT;
}

/**
 * True hvis brugeren er logget ind (uanset tier eller rolle). Bruges når
 * en feature blot kræver en konto (fx Indkøbsliste, Favoritter).
 */
export function isRegistered(user) {
  if (!user) return false;
  const role = (user.role || '').toLowerCase();
  // Alt andet end anonym-guest tælles som registreret.
  return role !== 'guest' || !!user.email || !!user.id;
}

/**
 * Hent hele abonnementsdefinitionen for en given user.
 */
export function getUserSubscription(user) {
  return SUBSCRIPTIONS[getSubscriptionType(user)];
}

/**
 * Skal denne bruger have vist upgrade-popup / abonnements-forslag?
 * Nej for Pro (færdigopgraderet) og employees (ansatte).
 */
export function canUpgrade(user) {
  if (isEmployee(user)) return false;
  const tier = getSubscriptionType(user);
  return tier !== SUBSCRIPTION_TYPES.PRO;
}
